import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/apiClient";
import SkillSlider from "@/components/SkillSlider";
import EducationSection, { blankDegree } from "@/components/EducationSection";
import { GraduationCap, Search, X, Check, Loader2, Sparkles, SlidersHorizontal } from "lucide-react";

const LEVEL_BY_IDX = ["none", "basic", "production"];

export default function Onboarding() {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [scores, setScores] = useState({ class_10_score: "", class_12_score: "", class_10_school: "", class_12_school: "" });
  const [degrees, setDegrees] = useState([blankDegree()]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [skills, setSkills] = useState([]);
  const [ratings, setRatings] = useState({}); // skill_id -> 0|1|2
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const search = useCallback(async (q) => {
    const { data } = await api.get(`/careers/roles`, { params: { search: q, limit: 10 } });
    setResults(data.roles);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(query), 250);
    return () => clearTimeout(t);
  }, [query, search]);

  const trackCount = new Set(selected.map((s) => s.career_id)).size;
  const toggle = (r) => {
    if (selected.find((s) => s.id === r.id)) {
      setSelected(selected.filter((s) => s.id !== r.id));
      return;
    }
    const isNewField = !selected.some((s) => s.career_id === r.career_id);
    if (isNewField && trackCount >= 5) return;
    setSelected([...selected, r]);
  };

  const goToAssessment = async () => {
    setStep(3);
    setLoadingSkills(true);
    try {
      const ids = [...new Set(selected.map((c) => c.career_id))].join(",");
      const { data } = await api.get("/skills/for-careers", { params: { career_ids: ids, limit: 14 } });
      setSkills(data.skills);
      setRatings(Object.fromEntries(data.skills.map((s) => [s.skill_id, 0])));
    } catch (err) {
      setError(apiErr(err));
    } finally {
      setLoadingSkills(false);
    }
  };

  const finish = async () => {
    setSaving(true);
    setError("");
    try {
      await api.put("/profile", { ...scores, education: degrees.filter((d) => d.degree) });
      const seen = new Set();
      const picks = selected.filter((r) => (seen.has(r.career_id) ? false : seen.add(r.career_id)));
      for (let i = 0; i < picks.length; i++) {
        await api.post("/tracks", { career_id: picks[i].career_id, priority_rank: i + 1, label: picks[i].role });
      }
      const assessments = Object.entries(ratings)
        .filter(([, v]) => v > 0)
        .map(([skill_id, v]) => ({ skill_id, level: LEVEL_BY_IDX[v] }));
      if (assessments.length) await api.post("/self-assessment", { assessments });
      await refreshUser();
      navigate("/tracks");
    } catch (err) {
      setError(apiErr(err));
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-2xl">
        <div className="flex items-center gap-2 justify-center mb-6">
          <div className="h-10 w-10 rounded-lg bg-[#6366F1] flex items-center justify-center">
            <GraduationCap size={22} className="text-white" />
          </div>
          <span className="font-extrabold tracking-tight text-xl text-white">Enjyme</span>
        </div>

        <div className="flex items-center gap-2 mb-6 justify-center">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`h-1.5 w-14 rounded-full transition-colors ${step >= s ? "bg-[#6366F1]" : "bg-slate-700"}`} />
          ))}
        </div>

        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-xl animate-fade-up">
          {step === 1 && (
            <>
              <p className="eyebrow text-[#6366F1]">STEP 1 OF 3</p>
              <h1 className="font-display font-extrabold text-2xl tracking-tight text-slate-900 mt-1">Your academics</h1>
              <p className="text-sm text-slate-500 mt-1 mb-6">These anchor your auto-generated resume and readiness baseline.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { k: "class_10_score", l: "Class 10 Score (%)", ph: "e.g. 92%" },
                  { k: "class_10_school", l: "Class 10 School (optional)", ph: "e.g. DPS" },
                  { k: "class_12_score", l: "Class 12 Score (%)", ph: "e.g. 88%" },
                  { k: "class_12_school", l: "Class 12 School (optional)", ph: "e.g. DPS" },
                ].map((f) => (
                  <div key={f.k}>
                    <label className="text-sm font-medium text-slate-700">{f.l}</label>
                    <input data-testid={`onb-${f.k}`} value={scores[f.k]} placeholder={f.ph}
                      onChange={(e) => setScores({ ...scores, [f.k]: e.target.value })}
                      className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-300 text-slate-900 focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/20 outline-none transition-colors" />
                  </div>
                ))}
              </div>
              <EducationSection degrees={degrees} setDegrees={setDegrees} />
              <button data-testid="onb-next" onClick={() => setStep(2)}
                className="mt-6 w-full bg-[#6366F1] hover:bg-[#4F46E5] active:scale-[0.98] text-white font-semibold py-2.5 rounded-lg transition-colors">
                Continue
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <p className="eyebrow text-[#6366F1]">STEP 2 OF 3</p>
              <h1 className="font-display font-extrabold text-2xl tracking-tight text-slate-900 mt-1">Pick your Dream Jobs</h1>
              <p className="text-sm text-slate-500 mt-1 mb-4">Select up to 5 roles you want to become. We'll build a readiness track for each.</p>

              {selected.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {selected.map((c, i) => (
                    <span key={c.id} data-testid={`selected-${i}`} className="inline-flex items-center gap-1.5 bg-[#6366F1] text-white text-sm font-medium px-3 py-1.5 rounded-full">
                      {c.role}
                      <button onClick={() => toggle(c)}><X size={14} /></button>
                    </span>
                  ))}
                </div>
              )}

              <div className="relative">
                <Search size={18} className="absolute left-3 top-3 text-slate-400" />
                <input data-testid="career-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search job titles..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-300 text-slate-900 focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/20 outline-none transition-colors" />
              </div>

              <div className="mt-3 max-h-64 overflow-y-auto space-y-1">
                {results.map((r, i) => {
                  const isSel = selected.some((s) => s.id === r.id);
                  const disabled = !isSel && !selected.some((s) => s.career_id === r.career_id) && trackCount >= 5;
                  return (
                    <button key={r.id} data-testid={`role-option-${i}`} onClick={() => toggle(r)} disabled={disabled}
                      className={`w-full text-left px-4 py-3 rounded-lg border transition-colors flex items-center justify-between ${
                        isSel ? "border-[#6366F1] bg-[#6366F1]/5" : "border-slate-200 hover:border-slate-300"
                      } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}>
                      <div>
                        <p className="font-semibold text-slate-900 text-sm">{r.role}</p>
                        <p className="text-xs text-slate-500">{r.field_label || r.field_name}</p>
                      </div>
                      {isSel && <Check size={18} className="text-[#6366F1]" />}
                    </button>
                  );
                })}
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={() => setStep(1)} className="px-5 py-2.5 rounded-lg border border-slate-300 text-slate-600 font-semibold hover:bg-slate-50 transition-colors">Back</button>
                <button data-testid="onb-to-assessment" onClick={goToAssessment} disabled={selected.length === 0}
                  className="flex-1 bg-[#6366F1] hover:bg-[#4F46E5] active:scale-[0.98] text-white font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-50">
                  Continue
                </button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <p className="eyebrow text-[#6366F1] flex items-center gap-1.5"><SlidersHorizontal size={13} /> STEP 3 OF 3</p>
              <h1 className="font-display font-extrabold text-2xl tracking-tight text-slate-900 mt-1">Rate your current skills</h1>
              <p className="text-sm text-slate-500 mt-1 mb-4">Slide each skill to your honest level. This seeds your baseline readiness — you can update it anytime.</p>

              {loadingSkills ? (
                <div className="h-52 flex items-center justify-center"><Loader2 className="animate-spin text-[#6366F1]" /></div>
              ) : (
                <div className="space-y-4 max-h-[380px] overflow-y-auto pr-1">
                  {skills.map((s) => (
                    <div key={s.skill_id} data-testid={`assess-row-${s.skill_id}`} className="p-4 rounded-xl border border-slate-200">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <p className="font-semibold text-slate-900 text-sm truncate">{s.skill_name}</p>
                          {s.essential && <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded shrink-0">ESSENTIAL</span>}
                        </div>
                        <span className="text-[11px] font-mono text-slate-400 shrink-0">w {s.weight}</span>
                      </div>
                      <SkillSlider value={ratings[s.skill_id] || 0} testid={`slider-${s.skill_id}`}
                        onChange={(v) => setRatings({ ...ratings, [s.skill_id]: v })} />
                    </div>
                  ))}
                  {skills.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No core skills found for these careers.</p>}
                </div>
              )}

              {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
              <div className="flex gap-3 mt-6">
                <button onClick={() => setStep(2)} className="px-5 py-2.5 rounded-lg border border-slate-300 text-slate-600 font-semibold hover:bg-slate-50 transition-colors">Back</button>
                <button data-testid="onb-finish" onClick={finish} disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] active:scale-[0.98] text-white font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-50">
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Launch my Career GPS
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
